import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { X, ShoppingBag, Trash2, Plus, Minus, ArrowRight } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { baseURL } from '../../utils/api';
import './MiniCart.css';

const MiniCart = ({ isOpen, onClose }) => {
    const { cartItems, updateQuantity, removeFromCart, getCartTotal } = useCart();
    const navigate = useNavigate();
    
    // Local state to track modified quantities before "Update"
    const [localQuantities, setLocalQuantities] = useState({});
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        const initialQs = {};
        cartItems.forEach(item => {
            initialQs[item.id] = item.quantity;
        });
        setLocalQuantities(initialQs);
        setHasChanges(false);
    }, [cartItems, isOpen]);

    const handleLocalQtyChange = (itemId, newQty) => {
        if (newQty < 1) return;
        setLocalQuantities(prev => ({ ...prev, [itemId]: newQty }));
        setHasChanges(true);
    };

    const handleUpdateCart = () => {
        Object.keys(localQuantities).forEach(id => {
            const item = cartItems.find(i => i.id === parseInt(id));
            if (item && item.quantity !== localQuantities[id]) {
                updateQuantity(parseInt(id), localQuantities[id]);
            }
        });
        setHasChanges(false);
    };

    const handleCheckout = () => {
        onClose();
        navigate('/checkout');
    };

    const handleViewCart = () => {
        onClose();
        navigate('/cart');
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="ec-minicart-overlay" onClick={onClose}></div>
            <div className={`ec-minicart-sidebar ${isOpen ? 'open' : ''}`}>
                <div className="ec-minicart-header">
                    <div className="ec-mch-title">
                        <ShoppingBag size={20} />
                        <span>Your Cart ({cartItems.length})</span>
                    </div>
                    <button className="ec-mch-close" onClick={onClose}>
                        <X size={24} />
                    </button>
                </div>

                <div className="ec-minicart-body">
                    {cartItems.length === 0 ? (
                        <div className="ec-minicart-empty">
                            <ShoppingBag size={48} />
                            <p>Your cart is empty</p>
                            <Link to="/shop" onClick={onClose} className="ec-shop-now-btn">Shop Now</Link>
                        </div>
                    ) : (
                        <div className="ec-minicart-list">
                            {cartItems.map(item => (
                                <div key={item.id} className="ec-minicart-item">
                                    <div className="ec-mci-img">
                                        <img src={item.image ? (item.image.startsWith('http') ? item.image : `${baseURL}${item.image}`) : 'https://via.placeholder.com/100'} alt={item.name} />
                                    </div>
                                    <div className="ec-mci-content">
                                        <div className="ec-mci-top">
                                            <Link to={`/product/${item.slug}`} className="ec-mci-name" onClick={onClose}>
                                                {item.name}
                                            </Link>
                                            <button className="ec-mci-del" onClick={() => removeFromCart(item.id)}>
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                        <div className="ec-mci-price">Rs. {item.price.toLocaleString()}</div>
                                        <div className="ec-mci-footer">
                                            <div className="ec-mci-qty">
                                                <button onClick={() => handleLocalQtyChange(item.id, (localQuantities[item.id] || item.quantity) - 1)}>
                                                    <Minus size={12} />
                                                </button>
                                                <span>{localQuantities[item.id] || item.quantity}</span>
                                                <button onClick={() => handleLocalQtyChange(item.id, (localQuantities[item.id] || item.quantity) + 1)}>
                                                    <Plus size={12} />
                                                </button>
                                            </div>
                                            <div className="ec-mci-total">
                                                Rs. {((item.price) * (localQuantities[item.id] || item.quantity)).toLocaleString()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {cartItems.length > 0 && (
                    <div className="ec-minicart-footer">
                        {hasChanges && (
                            <button className="ec-update-cart-btn" onClick={handleUpdateCart}>
                                Update Cart
                            </button>
                        )}
                        <div className="ec-mcf-subtotal">
                            <span>Subtotal</span>
                            <span>Rs. {getCartTotal().toLocaleString()}</span>
                        </div>
                        <div className="ec-mcf-actions">
                            <button className="ec-mcf-btn secondary" onClick={handleViewCart}>
                                View Cart
                            </button>
                            <button className="ec-mcf-btn primary" onClick={handleCheckout}>
                                Checkout <ArrowRight size={18} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default MiniCart;
